import React, { useState, useRef, useEffect } from 'react'
import { motion, useScroll, useTransform, useInView } from 'framer-motion'
import { Mail, Globe, MessageCircle } from 'lucide-react'
import { programmingSkills } from '../data/skills'

function WordReveal({ word, index, totalWords, scrollYProgress }) {
  const start = index / totalWords
  const end = (index + 1) / totalWords
  const opacity = useTransform(scrollYProgress, [start, end], [0.5, 1])
  const color = useTransform(scrollYProgress, [start, end], ['rgba(255,255,255,0.7)', 'rgba(255,255,255,1)'])
  return (
    <motion.span
      style={{ opacity, color, display: 'inline-block', marginRight: '0.3em' }}
    >
      {word}
    </motion.span>
  )
}

function ScrollRevealText({ text }) {
  const words = text.split(' ')
  const ref = useRef(null)
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start 0.9', 'end 0.3']
  })

  return (
    <p ref={ref} style={{
      fontSize: 'clamp(1rem, 2.2vw, 1.25rem)',
      lineHeight: 1.9,
      maxWidth: '620px',
      margin: '0 auto',
      textAlign: 'left'
    }}>
      {words.map((word, i) => (
        <WordReveal key={i} word={word} index={i} totalWords={words.length} scrollYProgress={scrollYProgress} />
      ))}
    </p>
  )
}

export default function About() {
  const headerRef = useRef(null)
  const bioRef = useRef(null)
  const skillsRef = useRef(null)
  const connectRef = useRef(null)

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', fn)
    return () => window.removeEventListener('resize', fn)
  }, [])

  const headerInView = useInView(headerRef, { once: false, amount: 0.15 })
  const bioInView = useInView(bioRef, { once: false, amount: 0.15 })
  const skillsInView = useInView(skillsRef, { once: false, amount: 0.15 })
  const connectInView = useInView(connectRef, { once: false, amount: 0.15 })

  const [showForm, setShowForm] = useState(false)
  const [formData, setFormData] = useState({ name: '', email: '', message: '' })
  const [formError, setFormError] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [sending, setSending] = useState(false)
  const [typoSuggestion, setTypoSuggestion] = useState('')
  const [emailValid, setEmailValid] = useState(false)
  const emailValidRef = useRef(false)

  const rolePrefixes = ['admin', 'info', 'support', 'contact', 'webmaster', 'noreply', 'no-reply', 'help', 'sales', 'abuse', 'postmaster']

  const gmailTypos = ['gmal.com', 'gmai.com', 'gamil.com', 'gmil.com', 'gmaill.com', 'gnail.com', 'gmaile.com']

  const checkMX = async (domain) => {
    try {
      const res = await fetch(`https://dns.google/resolve?name=${domain}&type=MX`)
      const data = await res.json()
      return data && data.Answer && data.Answer.length > 0
    } catch { return false }
  }

  const validateEmail = async (email) => {
    if (!email.trim()) return 'Email is required'
    const re = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/
    if (!re.test(email)) return 'Enter a valid email address'
    const [local, domain] = email.split('@')
    const domainLower = domain.toLowerCase()
    const prefix = local.toLowerCase()
    if (rolePrefixes.includes(prefix)) return 'Use a personal email, not a role-based one'
    if (gmailTypos.includes(domainLower)) return 'Did you mean gmail.com?'
    if (domainLower !== 'gmail.com') return 'Only gmail.com emails are accepted'
    const hasMX = await checkMX(domainLower)
    if (!hasMX) return 'Email domain does not accept mail'
    return ''
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!emailValidRef.current) return
    setFormError('Checking email...')
    const err = await validateEmail(formData.email)
    if (err) { setFormError(err); return }
    setFormError('')
    setSending(true)
    try {
      const res = await fetch('https://formspree.io/f/maqvryqe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      })
      if (res.ok) {
        setSubmitted(true)
        setFormData({ name: '', email: '', message: '' })
      } else {
        setFormError('Submission failed. Try again.')
      }
    } catch {
      setFormError('Network error. Check your connection.')
    }
    setSending(false)
  }

  const checkEmailSync = (email) => {
    if (!email.trim()) { setEmailValid(false); setTypoSuggestion(''); return }
    const re = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/
    if (!re.test(email)) { setEmailValid(false); setTypoSuggestion(''); return }
    const domain = email.split('@')[1].toLowerCase()
    const local = email.split('@')[0].toLowerCase()
    if (rolePrefixes.includes(local)) { setEmailValid(false); setTypoSuggestion(''); return }
    if (gmailTypos.includes(domain)) { setEmailValid(false); setTypoSuggestion('Did you mean gmail.com?'); return }
    if (domain !== 'gmail.com') { setEmailValid(false); setTypoSuggestion('Only gmail.com emails are accepted'); return }
    setEmailValid(true)
    setTypoSuggestion('')
    emailValidRef.current = true
  }

  const inputStyle = {
    width: '100%',
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(0,217,255,0.2)',
    borderRadius: '8px',
    padding: '10px 14px',
    color: 'white',
    fontFamily: 'monospace',
    fontSize: '0.8rem',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border 0.2s ease',
  }

  return (
    <div style={{ minHeight: '100vh', position: 'relative', overflow: 'hidden', padding: isMobile ? '80px 1rem 3rem' : '100px 2rem' }}>

      <div className="max-w-4xl mx-auto px-4" style={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          ref={headerRef}
          animate={headerInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.4 }}
          className="text-center mb-12"
        >
          <motion.h1
            animate={headerInView ? { opacity: 1, y: 0 } : { opacity: 0, y: -30, scale: 0.97 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            style={{
              fontSize: 'clamp(4rem, 12vw, 10rem)',
              fontWeight: 900,
              letterSpacing: '-0.02em',
              textTransform: 'uppercase',
              lineHeight: 0.9,
              color: 'white',
              margin: '0 0 3rem 0',
              textAlign: 'center',
            }}
          >About Me</motion.h1>
          <p style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'monospace', fontSize: '0.85rem', letterSpacing: '0.15em', textTransform: 'uppercase' }}>Developer · learner · tech enthusiast</p>
        </motion.div>

        <motion.div
          ref={bioRef}
          animate={bioInView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
        <div
          className="flex gap-12 mb-12"
          style={{
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'center' : 'flex-start',
            textAlign: isMobile ? 'center' : 'left'
          }}
        >
          <motion.div
            animate={bioInView ? { opacity: 1, x: 0, rotate: 0 } : { opacity: 0, x: -60, rotate: -5 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
            className="flex-shrink-0"
          >
            <motion.div
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
              style={{
                width: isMobile ? '120px' : '150px',
                height: isMobile ? '120px' : '150px',
                borderRadius: '50%',
                border: '2px solid #00d9ff',
                boxShadow: '0 0 0 4px rgba(0,217,255,0.1), 0 0 40px rgba(0,217,255,0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                background: 'rgba(0,217,255,0.05)',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <div style={{
                position: 'absolute', inset: 0,
                background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,217,255,0.03) 2px, rgba(0,217,255,0.03) 4px)',
                borderRadius: '50%',
              }} />
              <span style={{ fontSize: '2.5rem', fontWeight: 900, color: '#00d9ff', fontFamily: 'monospace', letterSpacing: '0.05em', zIndex: 1 }}>RD</span>
            </motion.div>
          </motion.div>

          <ScrollRevealText text="I'm Rohan Dalvi, a self-taught developer building real products from scratch — design, code, and deployment. I work across Flutter, Python, C, and React, and I integrate AI into what I build. I'm not interested in endless side projects that go nowhere. I build things that actually work and release them." />
        </div>

          <motion.div
            animate={bioInView ? 'visible' : 'hidden'}
            variants={{
              hidden: {},
              visible: { transition: { staggerChildren: 0.1, delayChildren: 0.2 } }
            }}
            className="flex justify-center gap-4"
            style={{
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: isMobile ? 'center' : undefined,
              width: isMobile ? '100%' : undefined
            }}
          >
            {[
              { icon: Mail, url: 'mailto:dalvirohan369@gmail.com', label: 'Email', hoverColor: '#00d9ff', hoverShadow: '0 0 12px rgba(0,217,255,0.3)' },
              { icon: Globe, url: 'https://linkedin.com/in/rohan-dalvi-552006377', label: 'LinkedIn', hoverColor: '#0077b5', hoverShadow: 'none' },
              { icon: MessageCircle, url: 'https://instagram.com/rohan_sys_', label: 'Instagram', hoverColor: '#ff006e', hoverShadow: 'none' },
            ].map((social) => {
              const Icon = social.icon
              return (
                <motion.a
                  key={social.label}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
                  whileHover={{ scale: 1.02, borderColor: social.hoverColor, color: social.hoverColor }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 24px',
                    background: 'transparent',
                    border: '1px solid rgba(255,255,255,0.15)',
                    color: 'rgba(255,255,255,0.5)',
                    fontWeight: 600,
                    borderRadius: '8px',
                    textDecoration: 'none',
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    width: isMobile ? '100%' : 'auto',
                    maxWidth: isMobile ? '240px' : 'none',
                    justifyContent: 'center'
                  }}
                >
                  <Icon size={18} />
                  {social.label}
                </motion.a>
              )
            })}
          </motion.div>
        </motion.div>

        <motion.div
          ref={skillsRef}
          animate={skillsInView ? 'visible' : 'hidden'}
          variants={{
            hidden: {},
            visible: { transition: { staggerChildren: 0.07 } }
          }}
          className="grid grid-cols-1 gap-12 mb-16"
        >
          <div>
            <h2 style={{ fontSize: '0.7rem', fontWeight: 700, color: 'rgba(255,255,255,0.3)', letterSpacing: '0.2em', textTransform: 'uppercase', fontFamily: 'monospace', marginBottom: '1.5rem' }}>Programming</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-w-2xl mx-auto">
              {programmingSkills.map((skill) => (
                <motion.div
                  key={skill}
                  variants={{ hidden: { opacity: 0, scale: 0.85 }, visible: { opacity: 1, scale: 1 } }}
                  whileHover={{ scale: 1.05, borderColor: '#00d9ff', boxShadow: '0 0 20px rgba(0,217,255,0.1)' }}
                  transition={{ duration: 0.15 }}
                  style={{
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderLeft: '3px solid #00d9ff',
                    borderRadius: '6px',
                    padding: isMobile ? '0.6rem 0.75rem' : '0.75rem 1rem',
                    textAlign: 'center',
                    cursor: 'default',
                  }}
                >
                  <p className="text-slate-100 font-semibold" style={{ fontSize: isMobile ? '0.75rem' : '0.85rem' }}>{skill}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        <motion.div
          ref={connectRef}
          animate={connectInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.4 }}
          style={{ background: 'rgba(0,217,255,0.06)', border: '1px solid rgba(0,217,255,0.25)', borderRadius: '16px', padding: '2rem', textAlign: 'center', boxShadow: '0 0 30px rgba(0,217,255,0.08)', position: 'relative', overflow: 'hidden' }}
        >
          <motion.h2
            animate={connectInView ? { opacity: 1, y: 0 } : { opacity: 0, y: -30, scale: 0.97 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            style={{
              fontSize: 'clamp(4rem, 12vw, 10rem)',
              fontWeight: 900,
              letterSpacing: '-0.02em',
              textTransform: 'uppercase',
              lineHeight: 0.9,
              color: 'white',
              margin: '0 0 3rem 0',
              textAlign: 'center',
            }}
          >Let's Connect</motion.h2>

          {!showForm && !submitted && (
            <>
              <p style={{ color: 'rgba(255,255,255,0.4)', marginBottom: '1.5rem', fontFamily: 'monospace', fontSize: '0.75rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Interested in discussing projects or collaborating?</p>
              <motion.button
                onClick={() => setShowForm(true)}
                whileHover={{ scale: 1.05, borderColor: '#00d9ff', background: 'rgba(0,217,255,0.06)' }}
                whileTap={{ scale: 0.95 }}
                style={{
                  border: '1px solid rgba(255,255,255,0.2)',
                  color: '#fff',
                  fontWeight: 600,
                  padding: '12px 32px',
                  borderRadius: '8px',
                  background: 'transparent',
                  cursor: 'pointer',
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                }}
              >
                Get In Touch
              </motion.button>
            </>
          )}

          {showForm && !submitted && (
            <form onSubmit={handleSubmit} style={{ maxWidth: '420px', margin: '0 auto', textAlign: 'left' }}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', color: 'rgba(255,255,255,0.3)', fontFamily: 'monospace', fontSize: '0.65rem', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: '0.35rem' }}>Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Your name (optional)"
                  style={inputStyle}
                  onFocus={(e) => e.target.style.borderColor = '#00d9ff'}
                  onBlur={(e) => e.target.style.borderColor = 'rgba(0,217,255,0.2)'}
                />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', color: 'rgba(255,255,255,0.3)', fontFamily: 'monospace', fontSize: '0.65rem', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: '0.35rem' }}>Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => { const val = e.target.value; setFormData({ ...formData, email: val }); setFormError(''); checkEmailSync(val) }}
                  placeholder="your@email.com"
                  style={inputStyle}
                  onFocus={(e) => e.target.style.borderColor = '#00d9ff'}
                  onBlur={(e) => e.target.style.borderColor = formError ? '#ff006e' : 'rgba(0,217,255,0.2)'}
                />
                {formError && <p style={{ color: '#ff006e', fontFamily: 'monospace', fontSize: '0.65rem', margin: '0.3rem 0 0', letterSpacing: '0.05em' }}>{formError}</p>}
                {typoSuggestion && <p style={{ color: '#00d9ff', fontFamily: 'monospace', fontSize: '0.65rem', margin: '0.3rem 0 0', letterSpacing: '0.05em' }}>{typoSuggestion}</p>}
              </div>
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', color: 'rgba(255,255,255,0.3)', fontFamily: 'monospace', fontSize: '0.65rem', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: '0.35rem' }}>Message</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Your message (optional)"
                  rows={3}
                  style={{ ...inputStyle, resize: 'vertical', minHeight: '60px' }}
                  onFocus={(e) => e.target.style.borderColor = '#00d9ff'}
                  onBlur={(e) => e.target.style.borderColor = 'rgba(0,217,255,0.2)'}
                />
              </div>
              <motion.button
                type="submit"
                disabled={sending || !emailValid}
                whileHover={!emailValid || sending ? {} : { scale: 1.03, borderColor: '#00d9ff', background: 'rgba(0,217,255,0.08)' }}
                whileTap={!emailValid || sending ? {} : { scale: 0.97 }}
                style={{
                  width: '100%',
                  border: `1px solid ${!emailValid ? 'rgba(255,255,255,0.1)' : 'rgba(0,217,255,0.4)'}`,
                  color: !emailValid ? 'rgba(255,255,255,0.2)' : '#00d9ff',
                  fontWeight: 600,
                  padding: '12px',
                  borderRadius: '8px',
                  background: !emailValid ? 'transparent' : 'rgba(0,217,255,0.05)',
                  cursor: sending ? 'not-allowed' : 'pointer',
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                  opacity: sending || !emailValid ? 0.4 : 1,
                }}
              >{sending ? 'Sending...' : 'Send Message'}</motion.button>

              <motion.button
                type="button"
                onClick={() => { setShowForm(false); setFormError('') }}
                whileHover={{ color: '#00d9ff' }}
                style={{
                  position: 'absolute',
                  bottom: '1rem',
                  left: '1rem',
                  background: 'none',
                  border: 'none',
                  color: 'rgba(255,255,255,0.2)',
                  fontFamily: 'monospace',
                  fontSize: '0.65rem',
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                  padding: 0,
                }}
              >← Back</motion.button>
            </form>
          )}

          {submitted && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              style={{ padding: '2rem 0' }}
            >
              <p style={{ color: '#00d9ff', fontFamily: 'monospace', fontSize: '1rem', letterSpacing: '0.1em', marginBottom: '1rem' }}>✓ Message sent!</p>
              <p style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'monospace', fontSize: '0.7rem', letterSpacing: '0.1em', marginBottom: '1.5rem' }}>Thanks for reaching out.</p>
              <motion.button
                onClick={() => { setSubmitted(false); setShowForm(false) }}
                whileHover={{ color: '#00d9ff' }}
                style={{
                  background: 'none',
                  border: '1px solid rgba(255,255,255,0.2)',
                  color: '#fff',
                  padding: '10px 28px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontFamily: 'monospace',
                  fontSize: '0.7rem',
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                }}
              >Send Another</motion.button>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  )
}
